/**
 * utils/pdfDossierBuilder.ts
 *
 * Core PDF manipulation utility for the "Constituer le dossier" feature.
 * Merges multiple PDFs into a single A4 document, optionally rescaling each
 * page to fit A4 with a right-side white margin, and drawing the lawyer's
 * stamp in the upper-right margin on every page of each piece.
 *
 * A4 dimensions in points (1pt = 1/72 inch): 595.28 × 841.89
 * The right margin reserved for the stamp is STAMP_MARGIN_WIDTH points wide.
 *
 * Architecture du tampon (refonte complète) :
 * - Le SVG est généré par stampSvgGenerator.ts (source unique de vérité)
 * - Il est rasterisé en PNG 400×400px via l'API Canvas du navigateur (≈288 DPI)
 * - Le PNG est embarqué dans le PDF via pdfDoc.embedPng()
 * - Il est dessiné via page.drawImage() dans le repère propre de la page A4
 *
 * Architecture des pages — trois chemins par ordre de priorité :
 * 1. Chemin principal : embedPdf + drawPage (pdf-lib, vectoriel).
 * 2. Chemin réparation : fixRawDeflateStreamsInDoc réencode les streams raw-deflate
 *    en zlib RFC 1950, puis embedPdf est retentée.
 * 3. Chemin rasterisation (fallback ultime) : pdf.js (pdfPageRasterizer.ts) rend
 *    chaque page sur un Canvas et l'exporte en JPEG. Fonctionne pour TOUT PDF
 *    lisible dans un navigateur, y compris les PDFs chiffrés (protection propriétaire,
 *    mot de passe vide) ou compressés avec des filtres exotiques. Le tampon est
 *    dessiné par-dessus l'image JPEG embarquée.
 *
 * Updated Apr 2026: refactored stamp to shared SVG generator + PNG rasterization.
 * Updated Apr 2026: replaced copyPages/scaleContent with embedPdf/drawPage.
 * Updated Apr 2026 (bugfixes): pieceIndexMap now built from completedPieces for
 *   contiguous stamp numbering; per-piece page insertion is now atomic (buffer
 *   all embedded pages before calling addPage so a mid-piece failure leaves no
 *   partial pages in the output).
 * Updated Apr 2026 (wizard preload): added preloadDossierPdfs export + preloadedBuffers
 *   option in BuildDossierOptions to reuse pre-fetched buffers and skip redundant downloads.
 * Updated Apr 2026: buildDossierPdf chunk loop now wraps each downloadPdf in try/catch so a
 *   single failed download does not abort the entire build; failed paths are tracked via
 *   downloadFailedPaths and surfaced in failedPieces when an entire piece is unresolvable.
 *   includeBordereau guard no longer requires stampConfig to be non-null.
 * Updated Apr 2026: bordereau rendering delegated to shared renderBordereauPages helper
 *   (bordereauPdfRenderer.ts); addBordereauPage removed. Any completed doc with a missing
 *   buffer now immediately fails the whole piece instead of being silently skipped.
 * Updated Jun 2026: PDFDocument.load now uses ignoreEncryption:true to silently handle
 *   owner-password-protected PDFs (common in French administrative documents). failedPieces
 *   now includes pieceName and reason ('download_failed'|'corrupted') for actionable UX.
 * Updated Jun 2026: fallback path now repairs raw-deflate content streams in-place
 *   (fixRawDeflateStreamsInDoc) before retrying embedPdf. copyPages replaced by pdf.js
 *   rasterisation as the last-resort fallback for encrypted/incompatible PDFs.
 */

import { PDFDocument, StandardFonts } from 'pdf-lib';
import type { PDFImage, PDFEmbeddedPage } from 'pdf-lib';
import { fixRawDeflateStreamsInDoc } from './pdfStreamFixer';
import { rasterizePdfToJpegs } from './pdfPageRasterizer';
import { supabase } from '../lib/supabase';
import type { DossierPiece } from '../types/dossier';
import { generateStampSvg } from './stampSvgGenerator';
import { renderBordereauPages } from './bordereauPdfRenderer';

export const A4_WIDTH = 595.28;
export const A4_HEIGHT = 841.89;
export const STAMP_MARGIN_WIDTH = 130;

const STAMP_SIZE_PT = 100;
const STAMP_RASTER_PX = 400;
const STAMP_CX = A4_WIDTH - STAMP_MARGIN_WIDTH / 2;
const STAMP_CY = A4_HEIGHT - 110;

export interface StampConfig {
  title: string;
  toqueNumber: string;
}

export interface BuildDossierOptions {
  pieces: DossierPiece[];
  stampConfig: StampConfig | null;
  includeBordereau: boolean;
  clientName: string | null;
  preloadedBuffers?: Map<string, ArrayBuffer>;
  onProgress?: (downloaded: number, total: number) => void;
}

/**
 * Prefetches all PDF buffers for the given pieces from Supabase Storage.
 * Returns a Map keyed by pdfPath. Intended for background preloading when
 * the user transitions to wizard step 2, so the download step is near-instant.
 */
export async function preloadDossierPdfs(
  pieces: DossierPiece[],
  onProgress?: (done: number, total: number) => void,
): Promise<Map<string, ArrayBuffer>> {
  const allPaths: string[] = [];
  for (const piece of pieces) {
    for (const doc of piece.docs) {
      if (doc.uploadStatus === 'completed' && doc.pdfPath) {
        allPaths.push(doc.pdfPath);
      }
    }
  }

  const total = allPaths.length;
  let done = 0;
  const result = new Map<string, ArrayBuffer>();
  const CONCURRENCY = 4;

  const chunks: string[][] = [];
  for (let i = 0; i < allPaths.length; i += CONCURRENCY) {
    chunks.push(allPaths.slice(i, i + CONCURRENCY));
  }

  for (const chunk of chunks) {
    await Promise.all(
      chunk.map(async (path) => {
        try {
          const buf = await downloadPdf(path);
          result.set(path, buf);
        } catch (err) {
          console.error('[preloadDossierPdfs] Failed to preload:', path, err);
        }
        done++;
        onProgress?.(done, total);
      })
    );
  }

  return result;
}

export interface BuildDossierResult {
  pdfBytes: Uint8Array;
  failedPieces: Array<{ pieceNumber: number; pieceName: string; reason: 'download_failed' | 'corrupted'; error: string }>;
}

async function downloadPdf(storagePath: string): Promise<ArrayBuffer> {
  const { data, error } = await supabase.storage
    .from('request-documents')
    .download(storagePath);
  if (error || !data) throw new Error(`Échec du téléchargement : ${storagePath}`);
  return data.arrayBuffer();
}

/**
 * Rasterizes an SVG string to a PNG Uint8Array via the browser Canvas API.
 * BROWSER-ONLY: requires document, Canvas, and Image APIs. Will throw in Node/SSR.
 */
function rasterizeStampSvg(svgMarkup: string, sizePx: number): Promise<Uint8Array> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('rasterizeStampSvg requires a browser environment (DOM unavailable)'));
  }
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = sizePx;
    canvas.height = sizePx;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas 2D context unavailable'));
      return;
    }

    const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      ctx.drawImage(img, 0, 0, sizePx, sizePx);
      URL.revokeObjectURL(url);
      canvas.toBlob((pngBlob) => {
        if (!pngBlob) {
          reject(new Error('Failed to create PNG blob from canvas'));
          return;
        }
        pngBlob.arrayBuffer().then((ab) => resolve(new Uint8Array(ab))).catch(reject);
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG as image'));
    };

    img.src = url;
  });
}


export async function buildDossierPdf(options: BuildDossierOptions): Promise<BuildDossierResult> {
  const { pieces, stampConfig, includeBordereau, preloadedBuffers, onProgress } = options;
  const withMargin = stampConfig !== null;

  const completedPieces = pieces.filter((p) =>
    p.docs.some((d) => d.uploadStatus === 'completed' && d.pdfPath)
  );

  const pieceIndexMap = new Map<string, number>(completedPieces.map((p, i) => [p.id, i + 1]));

  const allPaths: string[] = [];
  for (const piece of completedPieces) {
    for (const doc of piece.docs) {
      if (doc.uploadStatus === 'completed' && doc.pdfPath) {
        allPaths.push(doc.pdfPath);
      }
    }
  }

  const total = allPaths.length;
  let downloaded = 0;

  const CONCURRENCY = 4;
  const pathToBuffer = new Map<string, ArrayBuffer>(
    preloadedBuffers ? [...preloadedBuffers.entries()] : [],
  );
  const pathsToFetch = allPaths.filter((p) => !pathToBuffer.has(p));

  const chunks: string[][] = [];
  for (let i = 0; i < pathsToFetch.length; i += CONCURRENCY) {
    chunks.push(pathsToFetch.slice(i, i + CONCURRENCY));
  }

  downloaded = total - pathsToFetch.length;
  if (downloaded > 0) onProgress?.(downloaded, total);

  const downloadFailedPaths = new Set<string>();

  for (const chunk of chunks) {
    await Promise.all(
      chunk.map(async (path) => {
        try {
          const buf = await downloadPdf(path);
          pathToBuffer.set(path, buf);
        } catch (err) {
          console.error(`[dossier] Échec du téléchargement : ${path}`, err);
          downloadFailedPaths.add(path);
        }
        downloaded++;
        onProgress?.(downloaded, total);
      })
    );
  }

  const mergedDoc = await PDFDocument.create();

  const stampImages = new Map<number, PDFImage>();
  if (stampConfig) {
    for (const piece of completedPieces) {
      const num = pieceIndexMap.get(piece.id) ?? 0;
      if (!stampImages.has(num)) {
        const svgMarkup = generateStampSvg({
          title: stampConfig.title,
          toqueNumber: stampConfig.toqueNumber,
          pieceNumber: num,
          uniqueId: `stamp-${num}`,
          width: STAMP_RASTER_PX,
          height: STAMP_RASTER_PX,
        });
        const pngBytes = await rasterizeStampSvg(svgMarkup, STAMP_RASTER_PX);
        stampImages.set(num, await mergedDoc.embedPng(pngBytes));
      }
    }
  }

  const failedPieces: Array<{ pieceNumber: number; pieceName: string; reason: 'download_failed' | 'corrupted'; error: string }> = [];

  for (let pieceIdx = 0; pieceIdx < completedPieces.length; pieceIdx++) {
    const piece = completedPieces[pieceIdx];
    const pieceNumber = pieceIndexMap.get(piece.id) ?? pieceIdx + 1;
    const stampImage = stampImages.get(pieceNumber) ?? null;

    try {
      type EmbeddedEntry = { mode: 'embedded'; page: PDFEmbeddedPage; srcWidth: number; srcHeight: number };
      type RasterEntry   = { mode: 'raster';   image: PDFImage;       srcWidth: number; srcHeight: number };
      const bufferedPages: Array<EmbeddedEntry | RasterEntry> = [];
      let pieceFailed = false;

      for (const doc of piece.docs) {
        if (doc.uploadStatus !== 'completed' || !doc.pdfPath) continue;
        const buf = pathToBuffer.get(doc.pdfPath);
        if (!buf) {
          failedPieces.push({ pieceNumber, pieceName: piece.label, reason: 'download_failed', error: `Échec du téléchargement : ${doc.pdfPath}` });
          pieceFailed = true;
          break;
        }

        const srcDoc = await PDFDocument.load(buf, { ignoreEncryption: true });
        const pageIndices = srcDoc.getPageIndices();

        // Probe: embed into a throwaway doc and force PDFPageEmbedder.decodeContents()
        // before touching mergedDoc. PDFs with raw-deflate streams (broken zlib header,
        // common in French municipal software) fail here — we catch and fall back.
        let probeOk = false;
        try {
          const probeDoc = await PDFDocument.create();
          await probeDoc.embedPdf(srcDoc, pageIndices);
          await probeDoc.save();
          probeOk = true;
        } catch {
          // Stream is incompatible with pdf-lib's strict FlateStream parser; use fallback.
        }

        if (probeOk) {
          const embeddedPages = await mergedDoc.embedPdf(srcDoc, pageIndices);
          for (const ep of embeddedPages) {
            const { width, height } = ep.size();
            bufferedPages.push({ mode: 'embedded', page: ep, srcWidth: width, srcHeight: height });
          }
        } else {
          // Fallback: PDF has content streams with non-standard zlib headers (raw
          // deflate without RFC 1950 wrapper) that pdf-lib's strict FlateStream parser
          // rejects. Step 1: repair the streams in-place using the browser's native
          // DecompressionStream / CompressionStream APIs, then retry embedPdf.
          const fixedCount = await fixRawDeflateStreamsInDoc(srcDoc);
          console.log(`[dossier] Repaired ${fixedCount} raw-deflate stream(s), retrying embedPdf`);

          // Only retry embedPdf when streams were actually re-encoded to valid zlib.
          // embedPdf() is lazy — it defers content stream decoding to save(). If no
          // streams were fixed the retry silently "succeeds" here but mergedDoc.save()
          // later throws "Unknown compression method", killing the entire dossier.
          let repairEmbedOk = false;
          if (fixedCount > 0) {
            try {
              const embeddedPages = await mergedDoc.embedPdf(srcDoc, pageIndices);
              for (const ep of embeddedPages) {
                const { width, height } = ep.size();
                bufferedPages.push({ mode: 'embedded', page: ep, srcWidth: width, srcHeight: height });
              }
              repairEmbedOk = true;
            } catch {
              // Stream repair alone was not enough — fall through to rasterization.
            }
          }

          if (!repairEmbedOk) {
            // Last resort: pdf.js rasterises each page to JPEG at 200 DPI.
            // This handles ANY PDF that a browser can display: encrypted files
            // (owner-password with empty user password, common in French admin
            // documents), exotic filter chains, proprietary compressions, etc.
            // pdf.js performs full decryption and rendering; the result is
            // embedded as a JPEG image in a new A4 page with the stamp on top.
            console.log('[dossier] Rasterising via pdf.js (encrypted or incompatible PDF)');
            const jpegPages = await rasterizePdfToJpegs(buf);
            for (const jpegBytes of jpegPages) {
              const jpegImage = await mergedDoc.embedJpg(jpegBytes);
              const { width, height } = jpegImage.size();
              bufferedPages.push({ mode: 'raster', image: jpegImage, srcWidth: width, srcHeight: height });
            }
          }
        }
      }

      if (pieceFailed) continue;
      if (bufferedPages.length === 0) continue;

      for (const entry of bufferedPages) {
        const { srcWidth, srcHeight } = entry;
        const targetW = withMargin ? A4_WIDTH - STAMP_MARGIN_WIDTH : A4_WIDTH;
        const scaleX = targetW / srcWidth;
        const scaleY = A4_HEIGHT / srcHeight;
        const scale = Math.min(scaleX, scaleY, 1.0);
        const scaledW = srcWidth * scale;
        const scaledH = srcHeight * scale;
        const offsetX = (targetW - scaledW) / 2;
        const offsetY = (A4_HEIGHT - scaledH) / 2;

        const newPage = mergedDoc.addPage([A4_WIDTH, A4_HEIGHT]);

        if (entry.mode === 'embedded') {
          newPage.drawPage(entry.page, { x: offsetX, y: offsetY, width: scaledW, height: scaledH });
        } else {
          // Rasterised page: draw the JPEG image directly.
          newPage.drawImage(entry.image, { x: offsetX, y: offsetY, width: scaledW, height: scaledH });
        }

        if (withMargin && stampImage) {
          newPage.drawImage(stampImage, {
            x: STAMP_CX - STAMP_SIZE_PT / 2,
            y: STAMP_CY - STAMP_SIZE_PT / 2,
            width: STAMP_SIZE_PT,
            height: STAMP_SIZE_PT,
          });
        }
      }
    } catch (err) {
      console.error(`[dossier] Erreur lors du traitement de la pièce ${pieceNumber}:`, err);
      failedPieces.push({ pieceNumber, pieceName: piece.label, reason: 'corrupted', error: String(err) });
    }
  }

  if (includeBordereau) {
    const bordereauFont = await mergedDoc.embedFont(StandardFonts.Helvetica);
    const bordereauFontBold = await mergedDoc.embedFont(StandardFonts.HelveticaBold);
    renderBordereauPages({
      pdfDoc: mergedDoc,
      font: bordereauFont,
      fontBold: bordereauFontBold,
      pieces: completedPieces,
      insertAtIndex: 0,
    });
  }

  const pdfBytes = await mergedDoc.save();
  return { pdfBytes, failedPieces };
}
